import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { blogApi } from "../api/blogApi";
import { BlogPost, BlogCategory, CATEGORY_META } from "../types/Blog";
import {
  Code2,
  Building2,
  MessageSquare,
  BookOpen,
  Calendar,
  ArrowRight,
} from "lucide-react";

const CATEGORY_ICONS: Record<BlogCategory, React.ReactNode> = {
  algorithm: <Code2 className="w-3.5 h-3.5" />,
  "system-design": <Building2 className="w-3.5 h-3.5" />,
  behavioral: <MessageSquare className="w-3.5 h-3.5" />,
  general: <BookOpen className="w-3.5 h-3.5" />,
};

const CATEGORY_COLORS: Record<BlogCategory, string> = {
  algorithm: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  "system-design": "bg-blue-500/15 text-blue-400 border-blue-500/20",
  behavioral: "bg-purple-500/15 text-purple-400 border-purple-500/20",
  general: "bg-gray-500/15 text-gray-400 border-gray-500/20",
};

const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<BlogCategory | "all">("all");

  useEffect(() => {
    const loadPosts = async () => {
      setIsLoading(true);
      try {
        const data = await blogApi.getAllPublished();
        setPosts(data);
      } catch (error) {
        console.error("Error loading blog posts:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadPosts();
  }, []);

  const filteredPosts =
    activeCategory === "all"
      ? posts
      : posts.filter((p) => p.category === activeCategory);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Blog - AlgoBuddy</title>
        <meta
          name="description"
          content="Articles on algorithms, system design, and behavioral interview preparation."
        />
      </Helmet>

      <main className="min-h-screen bg-gray-950">
        {/* Header */}
        <div className="border-b border-gray-800/50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10">
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-3">
              Blog
            </h1>
            <p className="text-lg text-gray-500 max-w-xl">
              Insights on algorithms, system design, and acing technical interviews.
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="border-b border-gray-800/50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-1 -mb-px overflow-x-auto">
              <button
                onClick={() => setActiveCategory("all")}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeCategory === "all"
                    ? "border-white text-white"
                    : "border-transparent text-gray-500 hover:text-gray-300"
                }`}
              >
                All
              </button>
              {(Object.keys(CATEGORY_META) as BlogCategory[]).map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                    activeCategory === cat
                      ? "border-white text-white"
                      : "border-transparent text-gray-500 hover:text-gray-300"
                  }`}
                >
                  {CATEGORY_ICONS[cat]}
                  {CATEGORY_META[cat].label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Posts */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen className="w-10 h-10 text-gray-700 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                {posts.length === 0
                  ? "No blog posts yet."
                  : "No posts in this category."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredPosts.map((post) => (
                <article
                  key={post.id}
                  onClick={() => navigate(`/blog/${post.slug}`)}
                  className="group cursor-pointer"
                >
                  {post.coverImage && (
                    <div className="mb-4 overflow-hidden rounded-xl">
                      <img
                        src={post.coverImage}
                        alt={post.title}
                        className="w-full h-48 object-cover group-hover:scale-[1.02] transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2.5">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded border ${CATEGORY_COLORS[post.category]}`}
                      >
                        {CATEGORY_ICONS[post.category]}
                        {CATEGORY_META[post.category].label}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-600">
                        <Calendar className="w-3 h-3" />
                        {formatDate(post.createdAt)}
                      </span>
                    </div>
                    <h2 className="text-xl font-semibold text-white group-hover:text-emerald-400 transition-colors leading-snug">
                      {post.title}
                    </h2>
                    <p className="text-gray-500 text-sm leading-relaxed line-clamp-2">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center gap-1.5 text-sm text-gray-600 group-hover:text-emerald-400 transition-colors pt-1">
                      Read more
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default BlogPage;
